/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, EventEmitter, Output, Input, ChangeDetectorRef, ViewChild } from '@angular/core';

import { BaseInstance } from "../../../models/base-instance";
import { Function } from '../../../models/function';
import { FunctionParameter } from '../../../models/function-parameter';
import { GroupType } from "../../../models/group-type";
import { PropertyPatch } from "../../../models/property-patch";


import { ReaderService } from "../../../services/reader.service";
import { DraggableDirective } from 'app/directives/draggable.directive';
import { AntennaService } from 'app/services/antenna.service';
import { LocationService } from 'app/services/location.service';
import { ObjectService } from 'app/services/object.service';
import { ServiceService } from 'app/services/service.service';
import { TagService } from 'app/services/tag.service';
import { ZoneService } from 'app/services/zone.service';
declare var $: any;

/**
 * Popup Dialog used for the "Execute Function" Action on the Readers Page.
 */
@Component({
  selector: 'app-general-function-dialog',
  templateUrl: './general-function-dialog.component.html'
  //styleUrls: ['./general-dialog.component.css']
})

export class GeneralFunctionDialogComponent implements OnInit{

  @Input()  //Type of Instance whose Function is being modeled and could be called.  Used to figure out what Service to call
  instanceType: GroupType;

  @Input() // Instance the Function would be called on if called
  instance: BaseInstance;

  @Input()  //Function that is being modeled
  function: Function;

  /* 
  * Emitters used when user executes the function ( Unused as of now)
  */
  // @Output() //Called when the User starts executing a Function.  The function being executed is passed
  // functionExecutionStartEvent = new EventEmitter<Function>();

  // @Output() //Called when a Function finishes executing successfully
  // functionExecutionSuccessEvent = new EventEmitter<boolean>();

  // @Output() //Called when a Function finishes executing but with an error.  The error message is passed.
  // functionExecutionErrorEvent = new EventEmitter<string>();

  // Function Data, Holds the data for the parameters to call the function
  functionParameterData: FunctionParameterDataUi[] = [];

  @ViewChild
  (DraggableDirective) draggableDirective: DraggableDirective;

  @Output() 
  functionExecutedRefreshEvent = new EventEmitter<boolean>();


  constructor(
    private mAntennaService: AntennaService,
    private mLocationService: LocationService,
    private mObjectService: ObjectService,
    private mReaderService: ReaderService,
    private mServiceService: ServiceService,
    private mTagService: TagService,
    private mZoneService: ZoneService
    ){}

    ngOnInit(): void {
      this.refreshData();
      $("#generalDialog").show();
    }

  /**
 * Refreshes the Data for the Function.
 * 
 * This builds out the FunctionParameterDataUis (functionParameterData) and
 * adds a default DataObjectUi to each one representing the Field / Data the User
 * can enter for that Parameter.
 */
  refreshData(): void {
    const data: FunctionParameterDataUi[] = [];
    if (this?.function?.parameters?.length > 0) {
      for (const parameter of this.function.parameters) {
        const newData = new FunctionParameterDataUi();
        newData.functionParameter = parameter;
        const dataUi = new DataObjectUi();
        dataUi.value = "";
        newData.dataObjects.push(dataUi);
        data.push(newData);
      }
    }

    // Clear / Add the retrieve available parameters being used by the UI
    while (this.functionParameterData.length > 0) {
      this.functionParameterData.pop();
    }
    this.functionParameterData.push(...data);

  }

  /**
     * For use in Multi-Value Data for Function Parameters.
     * 
     * Adds a new possible value to the end of the current values
     * for the given FunctionParameerDataUi.
     */
  public addNewValue(dataUi: FunctionParameterDataUi): void {
    const dataObjectUi = new DataObjectUi();
    dataObjectUi.value = "";
    dataUi?.dataObjects?.push(dataObjectUi);
  }

  /**
   * For use in Multi-Value Data for Function Parameters.
   * 
   * Removes the value at the given index for the given FunctionParameterDataUi.
   */
  public removeValueAtIndex(dataUi: FunctionParameterDataUi, index: number) {
    dataUi.dataObjects.splice(index, 1);
  }

  /**
   * Returns a unique ID for use for the Function Parameters hidden <div> 
   * so that it can be shown when the button is clicked.
   */
  getFunctionParametersTabId(): string {
    return `function_${this.function.id}_parameters`;
  }

// Function called from UI after confirm is clicked
  executeFunctionClicked(): void {
    this.executeFunction();
  }

  private executeFunction(): void {
    //Convert the Function Parameter Data to the PropertyPatch[]
    this.hide();
    const data: PropertyPatch[] = [];
    for (const functionParameterData of this.functionParameterData) {
      for (const datum of functionParameterData.dataObjects) {
        if (datum?.value?.length > 0) {
          const propertyPatch = new PropertyPatch();
          propertyPatch.property = functionParameterData.functionParameter.key;
          propertyPatch.value = datum.value;
          data.push(propertyPatch);
        }
      }
    }
    //Let the Entity Instance Dialog know that a Function is starting to execute so that it can display the Function Execution Dialog
    //this.functionExecutionStartEvent.emit(this.function);
    console.log("Function Execution Started " + this.function.name);
    if (this.instanceType == null) {
      console.log("No Instance Type to determine what API to call...");
    } else {
        switch (this.instanceType) {
        case GroupType.ANTENNAINSTANCE: {
           // console.log("Antenna");
          this
            .mAntennaService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.LOCATIONINSTANCE: {
           // console.log("Location");
          this
            .mLocationService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.OBJECTINSTANCE: {
          //console.log("Object");
          this
            .mObjectService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.READERINSTANCE: {
         // console.log("Reader");
          this
            .mReaderService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.SERVICEINSTANCE: {
          //  console.log("Service");
          this
            .mServiceService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.TAG: {
           // console.log("Tag");
          this
            .mTagService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        case GroupType.READZONEINSTANCE: {
          //  console.log("Readerzone");
          this
            .mZoneService
            .executeFunction(this.instance.id, this.function.id, data)
            .then(
              success => this.handleSuccess(),
              error => this.handleError(error)
            );
          break;
        }
        default: {
          alert("Invalid Type to execute function on.");
          break;
        }
      }
  }
}

 private handleSuccess(): void {
    //Let the Entity Instance Dialog know that the Function has finished executing successfully so it can let the Function Execution Dialog know/be updated
    //this.functionExecutionSuccessEvent.emit(true);
    console.log("Function Executed Successfully");
    //If there were parameters, clear them and reset their data
    this.refreshData();
  }

 private handleError(error: any): void {
    console.error("Error: " + error);
    if (error != null) {
      console.log("Function Executed with Error");
      //this.functionExecutionErrorEvent.emit(error?.error);
    }
  }

    /**
     * Called to hide the Dialog
     */
    public hide(): void {
      $("#generalDialog").hide();
    }

}

export class FunctionParameterDataUi {
  functionParameter: FunctionParameter;
  dataObjects: DataObjectUi[] = [];
}

/**
 * UI Model for the Data of a Function Parameter (or Property for that matter)
 */
export class DataObjectUi {
  value: string;
  object_value: any;
}






